// LayoutEditorSharedToolbarLayer.jsx

import React from 'react';

// Utilities
import { getNodeBlockKey } from '../pageEditorLayoutUtils';

// Components
import LayoutEditorToolbarAction from '../toolbaraction/LayoutEditorToolbarAction';
import LayoutEditorSharedToolbar from './LayoutEditorSharedToolbar';

/**
 * LayoutEditorSharedToolbarLayer
 *
 * Resolves the active layout editor toolbar context into the floating shared toolbar UI.
 *
 * @param {object|null} blocksByKey - Block records keyed by block key.
 * @param {string} placement - Vertical placement relative to the anchor: above or below.
 * @param {object} positionStyle - Absolute positioning styles for the toolbar.
 * @param {React.RefObject} toolbarRef - Ref attached to the rendered shared toolbar root.
 * @param {object|null} toolbarContext - Active container and slot toolbar context from the editor shell.
 * @param {string} inlineRichTextSlotKey - Active inline rich-text slot key used to preserve editor focus.
 *
 * @returns {ReactNode} The rendered shared toolbar layer, or null when no toolbar context is active.
 */
const LayoutEditorSharedToolbarLayer = ({
	blocksByKey = {},
	placement = 'below',
	positionStyle = {},
	toolbarRef = null,
	toolbarContext = null,
	inlineRichTextSlotKey = '',
}) => {

	if (!toolbarContext) return null;

	const {
		activeSlotControls,
		activeSlotNode,
		containerKey,
		containerLabel,
		containerToolbarActions,
	} = toolbarContext;
	const activeSlotBlock = activeSlotNode?.kind === 'block_slot'
		? blocksByKey[getNodeBlockKey(activeSlotNode)] || null
		: null;
	const preserveEditorFocus = String(activeSlotBlock?.type || '').trim().toUpperCase() === 'RICH_TEXT'
		&& inlineRichTextSlotKey === activeSlotNode?.key;
	const resolvedContainerToolbarActions = Array.isArray(containerToolbarActions)
		? containerToolbarActions.filter(Boolean)
		: [];
	const resolvedPrimaryToolbarAction = activeSlotControls?.primaryToolbarAction || null;
	const resolvedSlotToolbarActions = Array.isArray(activeSlotControls?.slotToolbarActions)
		? activeSlotControls.slotToolbarActions.filter(Boolean)
		: [];
	const resolvedSlotToolbarAddActions = Array.isArray(activeSlotControls?.slotToolbarAddActions)
		? activeSlotControls.slotToolbarAddActions.filter(Boolean)
		: [];
	const resolvedSlotToolbarPageViewActions = Array.isArray(activeSlotControls?.slotToolbarPageViewActions)
		? activeSlotControls.slotToolbarPageViewActions.filter(Boolean)
		: [];
	const resolvedSlotToolbarStructureActions = Array.isArray(activeSlotControls?.slotToolbarStructureActions)
		? activeSlotControls.slotToolbarStructureActions.filter(Boolean)
		: [];
	const containerActionNodes = resolvedContainerToolbarActions.map((action, actionIndex) => (
		<LayoutEditorToolbarAction
			action={action}
			actionKey={`${containerKey}:container:${action?.iconKey || actionIndex}`}
			key={`${containerKey}:container:${action?.iconKey || actionIndex}`}
			addon="icon"
		/>
	));
	const blockActionNodes = activeSlotControls
		? [
			...resolvedSlotToolbarStructureActions.map((action, actionIndex) => (
				<LayoutEditorToolbarAction
					action={action}
					actionKey={`${activeSlotNode?.key || 'slot'}:split:${action?.label || action?.iconKey || actionIndex}`}
					key={`${activeSlotNode?.key || 'slot'}:split:${action?.label || action?.iconKey || actionIndex}`}
					addon="icon"
				/>
			)),
			...resolvedSlotToolbarActions
				.filter((action) => action !== resolvedPrimaryToolbarAction)
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:toolbar:${action?.iconKey || actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:toolbar:${action?.iconKey || actionIndex}`}
						addon="icon"
					/>
				)),
		]
		: [];
	const contentActionNodes = activeSlotControls
		? [
			...resolvedSlotToolbarAddActions
				.filter((action) => action?.label === 'Text')
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:content:text:${actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:content:text:${actionIndex}`}
						addon="icon"
					/>
				)),
			...resolvedSlotToolbarAddActions
				.filter((action) => action?.label === 'Fields')
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:content:fields:${actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:content:fields:${actionIndex}`}
						addon="icon"
					/>
				)),
			...resolvedSlotToolbarAddActions
				.filter((action) => action?.label === 'Data')
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:content:data:${actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:content:data:${actionIndex}`}
						addon="icon"
					/>
				)),
			...resolvedSlotToolbarAddActions
				.filter((action) => action?.label === 'View')
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:content:view:${actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:content:view:${actionIndex}`}
						addon="icon"
					/>
				)),
			...resolvedSlotToolbarAddActions
				.filter((action) => action?.label === 'Saved view' || action?.label === 'New view')
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:content:dashboard-view:${action?.label || actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:content:dashboard-view:${action?.label || actionIndex}`}
						addon="icon"
					/>
				)),
			...resolvedSlotToolbarPageViewActions.map((action, actionIndex) => (
				<LayoutEditorToolbarAction
					action={action}
					actionKey={`${activeSlotNode?.key || 'slot'}:page-view:${action?.label || action?.iconKey || actionIndex}`}
					key={`${activeSlotNode?.key || 'slot'}:page-view:${action?.label || action?.iconKey || actionIndex}`}
					addon="icon"
				/>
			)),
			...resolvedSlotToolbarAddActions
				.filter((action) => action?.label === 'Rollup')
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:content:rollup:${actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:content:rollup:${actionIndex}`}
						addon="icon"
					/>
				)),
			...resolvedSlotToolbarAddActions
				.filter((action) => action?.label === 'Discussion')
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:content:discussion:${actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:content:discussion:${actionIndex}`}
						addon="icon"
					/>
				)),
			...resolvedSlotToolbarAddActions
				.filter((action) => action?.label === 'Video')
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:content:video:${actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:content:video:${actionIndex}`}
						addon="icon"
					/>
				)),
			...resolvedSlotToolbarAddActions
				.filter((action) => action?.label === 'Cover')
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:content:cover:${actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:content:cover:${actionIndex}`}
						addon="icon"
					/>
				)),
			...resolvedSlotToolbarAddActions
				.filter((action) => action?.label === 'Zone')
				.map((action, actionIndex) => (
					<LayoutEditorToolbarAction
						action={action}
						actionKey={`${activeSlotNode?.key || 'slot'}:content:zone:${actionIndex}`}
						key={`${activeSlotNode?.key || 'slot'}:content:zone:${actionIndex}`}
						addon="icon"
					/>
				)),
			...(resolvedPrimaryToolbarAction ? [
				<LayoutEditorToolbarAction
					action={resolvedPrimaryToolbarAction}
					actionKey={`${activeSlotNode?.key || 'slot'}:content:primary`}
					key={`${activeSlotNode?.key || 'slot'}:content:primary`}
					addon="icon"
				/>,
			] : []),
		]
		: [];

	// ----- RENDER ----- //

	return (
		<LayoutEditorSharedToolbar
			blockActionNodes={blockActionNodes}
			containerActionNodes={containerActionNodes}
			containerLabel={containerLabel}
			contentActionNodes={contentActionNodes}
			placement={placement}
			positionStyle={positionStyle}
			preserveEditorFocus={preserveEditorFocus}
			ref={toolbarRef}
		/>
	);
};

// Dev Tools
LayoutEditorSharedToolbarLayer.displayName = 'LayoutEditorSharedToolbarLayer';

export default LayoutEditorSharedToolbarLayer;
